import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import { refresh } from '@/services/rbac-auth.service';
import { ROUTES } from '@/constants';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  withCredentials: true,                 // ← cookie HttpOnly del RBAC
  headers: { 'Content-Type': 'application/json' },
});

// Un 403 con `code` Fiado = denegación de permiso (NO sesión). Sin code / 401 → intentar refresh.
function shouldRefresh(error: AxiosError): boolean {
  const status = error.response?.status;
  if (status === 401) return true;
  if (status === 403) return !(error.response?.data as { code?: string } | undefined)?.code;
  return false;
}

let refreshPromise: Promise<boolean> | null = null;

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const url = original?.url ?? '';
    if (original && !original._retried && shouldRefresh(error)
        && !url.includes('/auth/') && !url.includes('/public/')) {
      original._retried = true;
      if (!refreshPromise) refreshPromise = refresh().finally(() => { refreshPromise = null; });
      const ok = await refreshPromise;
      if (ok) return api(original);       // retry UNA vez
      // el refresh falló → sesión muerta → logout real
      useAuthStore.getState().clearAuth();
      window.location.href = ROUTES.LOGIN;
    }
    return Promise.reject(error);
  },
);

// Fiado API wrapper
export interface ApiResponse<T> { code: string; data: T; description: string | null; msg: string | null; }
export const apiGet = <T>(url: string, config?: Parameters<typeof api.get>[1]) => api.get<ApiResponse<T>>(url, config).then((r) => r.data);
export const apiPost = <T>(url: string, data?: unknown, config?: Parameters<typeof api.post>[2]) => api.post<ApiResponse<T>>(url, data, config).then((r) => r.data);
export const apiPut = <T>(url: string, data?: unknown, config?: Parameters<typeof api.put>[2]) => api.put<ApiResponse<T>>(url, data, config).then((r) => r.data);
export const apiPatch = <T>(url: string, data?: unknown, config?: Parameters<typeof api.patch>[2]) => api.patch<ApiResponse<T>>(url, data, config).then((r) => r.data);
export const apiDelete = <T>(url: string, config?: Parameters<typeof api.delete>[1]) => api.delete<ApiResponse<T>>(url, config).then((r) => r.data);
